import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Box, Flex, Grid, Image, Text, VStack,
} from '@chakra-ui/react';

const clamp01 = (n) => Math.max(0, Math.min(1, n));
const toPos = (f) => `${(f.x * 100).toFixed(2)}% ${(f.y * 100).toFixed(2)}%`;

/**
 * Eventbrite-style focal-point picker. The user drags a target over the image
 * to choose which part stays visible when the cover is cropped into a card.
 * Returns the chosen point (normalized 0..1) via onApply.
 *
 * The point is resolution-independent, so picking it on a local preview is
 * valid for the server's aspect-preserving derivative.
 */
const EventCoverFocusModal = ({ isOpen, imageSrc, initialFocal, onApply, onClose }) => {
  const [focal, setFocal] = useState(initialFocal || { x: 0.5, y: 0.5 });
  const stageRef = useRef(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    if (isOpen) setFocal(initialFocal || { x: 0.5, y: 0.5 });
  }, [isOpen, initialFocal]);

  const setFromEvent = useCallback((clientX, clientY) => {
    const el = stageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    setFocal({
      x: clamp01((clientX - rect.left) / rect.width),
      y: clamp01((clientY - rect.top) / rect.height),
    });
  }, []);

  const onPointerDown = (e) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setFromEvent(e.clientX, e.clientY);
  };
  const onPointerMove = (e) => {
    if (draggingRef.current) setFromEvent(e.clientX, e.clientY);
  };
  const onPointerUp = (e) => {
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const objectPosition = toPos(focal);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered>
      <ModalOverlay bg="rgba(0,0,0,0.7)" backdropFilter="blur(2px)" />
      <ModalContent bg="#0f0f10" color="#fafafa" border="1px solid rgba(255,255,255,0.1)" borderRadius="16px">
        <ModalHeader fontSize="lg" fontWeight="700">
          Position your cover photo
          <Text fontSize="sm" fontWeight="400" color="rgba(255,255,255,0.5)" mt={1}>
            Drag the target to choose which part of the image stays in view.
          </Text>
        </ModalHeader>
        <ModalCloseButton color="rgba(255,255,255,0.6)" />

        <ModalBody>
          <Grid templateColumns={{ base: '1fr', md: '1.4fr 1fr' }} gap={5} alignItems="start">
            {/* Editor */}
            <Box
              ref={stageRef}
              position="relative"
              borderRadius="12px"
              overflow="hidden"
              bg="#000"
              userSelect="none"
              cursor="crosshair"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              sx={{ touchAction: 'none' }}
            >
              <Image
                src={imageSrc}
                alt="Cover"
                w="100%"
                maxH="360px"
                objectFit="contain"
                draggable={false}
                pointerEvents="none"
              />
              {/* Target reticle */}
              <Box
                position="absolute"
                left={`${focal.x * 100}%`}
                top={`${focal.y * 100}%`}
                transform="translate(-50%, -50%)"
                w="36px"
                h="36px"
                borderRadius="full"
                border="2px solid #fff"
                boxShadow="0 0 0 2px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(0,0,0,0.3)"
                pointerEvents="none"
              >
                <Box position="absolute" left="50%" top="-9px" w="2px" h="9px" bg="#fff" transform="translateX(-50%)" />
                <Box position="absolute" left="50%" bottom="-9px" w="2px" h="9px" bg="#fff" transform="translateX(-50%)" />
                <Box position="absolute" top="50%" left="-9px" h="2px" w="9px" bg="#fff" transform="translateY(-50%)" />
                <Box position="absolute" top="50%" right="-9px" h="2px" w="9px" bg="#fff" transform="translateY(-50%)" />
              </Box>
            </Box>

            {/* Live previews */}
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" fontWeight="600" color="rgba(255,255,255,0.85)">
                Preview
              </Text>
              {/* Primary — exactly the 2:1 used on the card, form, and event page */}
              <Box>
                <Text fontSize="xs" color="rgba(255,255,255,0.45)" mb={1.5}>Cover (2:1) — what appears everywhere</Text>
                <Box w="100%" borderRadius="10px" overflow="hidden" border="1px solid rgba(255,255,255,0.1)" sx={{ aspectRatio: '2 / 1' }}>
                  <Image src={imageSrc} alt="" w="100%" h="100%" objectFit="cover" objectPosition={objectPosition} />
                </Box>
              </Box>
              {/* Secondary reference */}
              <Box flex="0 0 auto">
                <Text fontSize="xs" color="rgba(255,255,255,0.45)" mb={1.5}>Square (reference)</Text>
                <Box w="84px" h="84px" borderRadius="10px" overflow="hidden" border="1px solid rgba(255,255,255,0.1)">
                  <Image src={imageSrc} alt="" w="100%" h="100%" objectFit="cover" objectPosition={objectPosition} />
                </Box>
              </Box>
            </VStack>
          </Grid>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button
            variant="ghost"
            color="rgba(255,255,255,0.7)"
            _hover={{ bg: 'rgba(255,255,255,0.06)' }}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="ghost"
            size="sm"
            color="#fafafa"
            borderRadius="full"
            fontSize="sm"
            border="1px solid rgba(255,255,255,0.12)"
            _hover={{ bg: 'rgba(255,255,255,0.06)' }}
            _focus={{ boxShadow: 'none' }}
            onClick={() => onApply?.(focal)}
          >
            Apply
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EventCoverFocusModal;
